import {Field} from 'react-final-form';
import {Navigate, useLocation, useParams} from 'react-router-dom';
import {Tooltip} from 'react-tooltip';

import DefaultWeatherDisplaySelector from 'src/components/DefaultWeatherDisplaySelector';
import LanguageSelector from 'src/components/LangaugeSelector';
import PageTitle from 'src/components/PageTitle';
import {useTranslations} from 'src/components/TranslationsContext';
import {useCurrentOrganization} from 'src/hooks/useAuth';
import {
  useUserChangeRoleMutation,
  useUserDeleteMutation,
  useUserUpdateMutation,
} from 'src/hooks/useOrganizationUsers';
import {ApiOrganization, ApiUserProfile} from 'src/lib/api/auth';
import {LOGIN_APP_URL, makeUuidPrefix} from 'src/lib/routeUtils';
import * as tw from 'src/lib/tailwindClasses';
import FormSection, {FormSectionSeparator} from 'src/pages/Settings/FormSection';

import RoleDescription from './RoleDescription';

type WrapperProps = {
  currentUser: ApiUserProfile;
  users: ApiUserProfile[];
  organization: ApiOrganization;
};

function findUser(users: ApiUserProfile[], userId: ApiUserProfile['id']) {
  return users.find((u: ApiUserProfile) => u.id === userId);
}

export default function UserWrapper({currentUser, users, organization}: WrapperProps) {
  const urlParams = useParams<{userId?: ApiUserProfile['id']}>();
  const user = findUser(users, urlParams.userId!);

  if (!user) {
    // eslint-disable-next-line no-console
    console.log(`Could not find user ${urlParams.userId}`, user);
    return <Navigate to={`/${makeUuidPrefix(organization.id)}/settings/users`} replace />;
  }

  return <User currentUser={currentUser} user={user} />;
}

type Props = {
  currentUser: ApiUserProfile;
  user: ApiUserProfile;
};

export function User({currentUser, user}: Props) {
  const {t} = useTranslations();
  const {pathname} = useLocation();
  const currentOrg = useCurrentOrganization()!;
  const userIsSelf = currentUser.id === user.id;
  // this is also enforced in the api, but this lets us visually show if they can edit or not
  const canEdit = currentUser.role === 'admin' || currentUser.role === 'owner' || userIsSelf;
  // Resetting MFA requires the requesting user to be an owner of the target user's org
  // (see backend user.reset_mfa). The login app re-checks this server-side.
  const isOwner = currentUser.role === 'owner';

  const updateUser = useUserUpdateMutation(user, currentOrg.id);
  const changeUserRole = useUserChangeRoleMutation(user, currentOrg.id);
  const deleteUser = useUserDeleteMutation(user);

  return (
    <>
      <PageTitle
        pageStack={[
          {title: currentOrg.name, to: '/:currentOrgIdPrefix/projects'},
          {title: t('settings'), to: '/:currentOrgIdPrefix/settings'},
          {title: t('team'), to: '/:currentOrgIdPrefix/settings/users'},
          {title: user.name, to: pathname},
        ]}
      />
      <FormSection
        name={t('userDetails')}
        isLoading={updateUser.isLoading}
        initialValues={{
          name: user.name,
        }}
        onSubmit={(values) => {
          updateUser.mutate({name: values.name});
        }}
        hasPermissions={canEdit}
        render={() => {
          return (
            <>
              <div className="col-span-6 sm:col-span-3">
                <label htmlFor="name-input" className="block text-sm font-medium text-gray-700">
                  {t('name')}
                </label>
                <Field name="name">
                  {({input}) => {
                    return (
                      <input
                        id="name-input"
                        type="text"
                        className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                        disabled={!canEdit}
                        {...input}
                      />
                    );
                  }}
                </Field>
              </div>
              <div className="col-span-6 sm:col-span-4">
                <label htmlFor="email-input" className="block text-sm font-medium text-gray-700">
                  {t('emailAddress')}
                </label>
                <input
                  disabled
                  readOnly
                  id="email-input"
                  type="email"
                  value={user.email}
                  autoComplete="email"
                  className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                />
              </div>
              <div className="col-span-6 sm:col-span-3">
                <label
                  htmlFor="organization-name-input"
                  className="block text-sm font-medium text-gray-700"
                >
                  {t('organization')}
                </label>
                <input
                  value={currentOrg.name}
                  readOnly
                  disabled
                  type="text"
                  id="organization-name-input"
                  className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                />
              </div>
            </>
          );
        }}
      />

      {userIsSelf && (
        <>
          <FormSectionSeparator />
          <SectionWithoutForm
            name={t('authentication')}
            description={t('authenticationDescription')}
          >
            <button
              className={`${tw.buttonPrimary}`}
              onClick={() => {
                // Open Update Email page in new tab
                window.open(
                  `${LOGIN_APP_URL}/updateEmail?redirect=${window.location.href}`,
                  '_blank'
                );
              }}
            >
              {t('updateEmail')}
            </button>
            <button
              className={`${tw.buttonPrimary}`}
              onClick={() => {
                // Open MFA Settings in new tab
                window.open(
                  `${LOGIN_APP_URL}/mfaSettings?redirect=${window.location.href}`,
                  '_blank'
                );
              }}
            >
              {t('configureMFA')}
            </button>
          </SectionWithoutForm>

          <FormSectionSeparator />
          <LanguageSelector />

          <FormSectionSeparator />
          <DefaultWeatherDisplaySelector currentUser={currentUser} />
        </>
      )}

      <FormSectionSeparator />

      <FormSection
        name={t('permissions')}
        isLoading={changeUserRole.isLoading}
        hasPermissions={canEdit}
        description={<RoleDescription />}
        onSubmit={(values) => {
          if (values.role === 'owner') {
            if (window.confirm(t('confirmPromoteOwner'))) {
              window.location.assign(
                `${LOGIN_APP_URL}/promoteToOwner?userId=${user.id}&organizationId=${currentOrg.id}&redirect=${window.location.href}`
              );
            }
            return;
          }
          changeUserRole.mutate(values.role);
        }}
        initialValues={{role: user.role}}
        render={() => {
          return (
            <div className="col-span-6 sm:col-span-3">
              <label htmlFor="role" className="block text-sm font-medium text-gray-700">
                {t('role')}
              </label>
              <Field name="role" component="select" className={tw.formSelect}>
                {currentUser.role === 'owner' && <option value="owner">{t('ownerRole')}</option>}
                {(currentUser.role === 'owner' || currentUser.role === 'admin') && (
                  <option value="admin">{t('adminRole')}</option>
                )}
                {(currentUser.role === 'owner' ||
                  currentUser.role === 'admin' ||
                  currentUser.role === 'regular') && (
                  <option value="regular">{t('regularRole')}</option>
                )}
                <option value="readonly">{t('readonlyRole')}</option>
              </Field>
            </div>
          );
        }}
      />

      {!userIsSelf && (
        <>
          <FormSectionSeparator />
          <SectionWithoutForm name={t('dangerZone')} description={t('dangerZoneWarning')}>
            <span
              {...(!isOwner && {
                'data-tooltip-id': 'reset-mfa-tooltip',
                'data-tooltip-content': t('resetMfaOwnerOnly'),
              })}
            >
              <button
                className={`${tw.buttonSecondary}`}
                disabled={!isOwner}
                onClick={() => {
                  // Hand off to the login app, which reauthenticates the requesting user,
                  // resets the target user's MFA, then redirects back here. We pass orgId
                  // so the login app can look up the target user's name/email
                  window.location.assign(
                    `${LOGIN_APP_URL}/resetMfa?userId=${user.id}&orgId=${currentOrg.id}&redirect=${window.location.href}`
                  );
                }}
              >
                {t('resetMfa')}
              </button>
            </span>
            {!isOwner && <Tooltip id="reset-mfa-tooltip" />}
            <span
              {...(!isOwner && {
                'data-tooltip-id': 'reset-credentials-tooltip',
                'data-tooltip-content': t('resetCredentialsOwnerOnly'),
              })}
            >
              <button
                className={`${tw.buttonSecondary}`}
                disabled={!isOwner}
                onClick={() => {
                  // Hand off to the login app, which reauthenticates the requesting user,
                  // logs the target user out and resets their password, then redirects back here.
                  // We pass orgId so the login app can look up the target user's name/email.
                  window.location.assign(
                    `${LOGIN_APP_URL}/credentialsReset?userId=${user.id}&orgId=${currentOrg.id}&redirect=${window.location.href}`
                  );
                }}
              >
                {t('resetCredentials')}
              </button>
            </span>
            {!isOwner && <Tooltip id="reset-credentials-tooltip" />}
            <button
              className={`${tw.buttonDanger}`}
              // don't allow self-deletion and make sure user has permissions
              disabled={!canEdit || userIsSelf || deleteUser.isLoading}
              onClick={() => {
                if (window.prompt(`${t('deleteConfirmPrompt', {name: user.name})}`) === user.name) {
                  deleteUser.mutate(null);
                }
              }}
            >
              {t('deleteUser')}
            </button>
          </SectionWithoutForm>
        </>
      )}
    </>
  );
}

const SectionWithoutForm = ({
  name,
  description,
  children,
}: {
  name: string;
  description: string;
  children: React.ReactNode;
}) => {
  return (
    <div>
      <div className="md:grid md:grid-cols-3 md:gap-6">
        <div className="md:col-span-1">
          <div className="px-4 sm:px-0">
            <h3 className="text-lg font-medium leading-6 text-gray-900">{name}</h3>
            <div className="mt-1 text-sm text-gray-600">{description}</div>
          </div>
        </div>
        <div className="flex flex-col gap-4 items-start">{children}</div>
      </div>
    </div>
  );
};
